import { useState, useEffect, useReducer } from 'react';
import { useNotificationProvider } from 'hooks';
import { createUserApi, getAllContactsApi } from 'api';
import { getContactApi, getUsersApi } from 'api';
import userAddReducer from 'containers/pages/users/reducers/userAddReducer';
import { initUser, initValid } from 'containers/pages/users/data';
import { toggleAddressData } from 'containers/pages/contacts/data';
import {
	addCustName,
	addFileId,
	convertExistList,
} from 'containers/pages/users/libs';
import { filterContacts } from 'containers/pages/contacts/libs';
import { convertFromContactToUserObj } from 'containers/pages/users/libs';
import {
	handleUser,
	makeUserObj,
	mergePhone,
	checkUserValid,
} from 'containers/pages/users/libs';
import {
	F_NAME,
	L_NAME,
	RESET,
	USER_CONVERT,
} from 'containers/pages/users/constants';
import { NOTIF_USER_CREATE_SUCCESS } from 'constants/notificationMsg';
import { useDispatch, useSelector } from 'react-redux';
import { storeUsers } from 'redux/users/usersSlice';
import {
	storeSearchedUsers,
	storeSearchKeyword,
} from 'redux/userMgt/userMgtSlice';
import { storeAllContacts } from 'redux/userMgt/userMgtSlice';
import {
	selectAllContacts,
	storeExistingContacts,
} from 'redux/userMgt/userMgtSlice';
import { selectAllCompanies } from 'redux/companies/companiesSlice';
import { selectAllContactTypes } from 'redux/contactTypes/contactTypesSlice';
import { selectAllLanguages } from 'redux/contacts/languagesSlice';
import { setUserFolderPermissions, prepareFolderPermissionsPayload } from 'api/users/folderPermissionsApi';
import { getActiveTourKey } from 'components/onboarding/tour/activeTour';

// When the "Add a user" guided tour is running we walk through the whole form but
// must NOT persist — otherwise every run leaves a junk test user in the DB.
const ADD_USER_TOUR_KEY = 'canapps_add_user';

/**
 * @function useUserAddForm
 * @param {string} id
 * @param {function} onClickForm
 * @param {function} handleMode
 * @returns {object}
 */
const useUserAddForm = (id, onClickForm, handleMode) => {
	const reduxDispatch = useDispatch();
	const allContacts = useSelector(selectAllContacts);
	const { companies } = useSelector(selectAllCompanies);
	const { contactTypes } = useSelector(selectAllContactTypes);
	const { languages } = useSelector(selectAllLanguages);
	const [user, dispatch] = useReducer(userAddReducer, initUser);
	const { notifySuccess, notifyError } = useNotificationProvider();
	const [isLoading, setIsLoading] = useState(false);
	const [valid, setValid] = useState(initValid);
	const [addressType, setAddressType] = useState(
		toggleAddressData.buttons[0].value,
	);

	const onTogBtn = (e) => {
		setAddressType(e.target.value);
	};

	const handleForm = async (e, payload) => {
		handleUser(
			e,
			payload,
			user,
			addressType,
			dispatch,
			setValid,
			handleMode,
		);
		if (e.target.name === F_NAME || e.target.name === L_NAME) {
			const filtered = filterContacts(e.target.value, allContacts);
			reduxDispatch(storeExistingContacts(filtered));
		}
	};

	const onResetBtn = () => {
		dispatch({ type: RESET, payload: initUser });
		setValid(initValid);
		handleMode(true);
		reduxDispatch(storeExistingContacts([]));
	};

	const onValid = (key, error, msg) => {
		if (error) {
			setValid((prev) => ({
				...prev,
				[key]: { isErr: error, errMsg: msg, canNotSave: true },
			}));
		} else {
			setValid((prev) => ({
				...prev,
				[key]: { isErr: error, errMsg: msg, canNotSave: false },
			}));
		}
	};

	const initAfterAddingUser = async () => {
		try {
			const tempUsers = await getUsersApi();
			const merged = await mergePhone(tempUsers);
			const _users = await addCustName(merged, companies);
			onClickForm();
			reduxDispatch(storeUsers(_users));
			reduxDispatch(storeSearchedUsers(_users));
			reduxDispatch(storeSearchKeyword(''));
		} catch (error) {
			notifyError(error.message);
		}
	};

	const onCreateUserWithPermissions = async (draftFolderPermissions = []) => {
		try {
			if (!validateBeforeSaving()) return;

			setIsLoading(true);

			// Guided tour: don't hit the create API (no test data in the DB). Still
			// close the form + refresh the list so the walkthrough advances the same
			// way a real save would (SAVE -> FINISH on form close), then reset.
			if (getActiveTourKey() === ADD_USER_TOUR_KEY) {
				notifySuccess(
					'Sandbox walkthrough complete—no user was created.',
				);
				await initAfterAddingUser();
				dispatch({ type: RESET, payload: initUser });
				return;
			}

			let _user = makeUserObj(user);
			const added = await addFileId(_user);
			
			// 1. Create user first (create user API returns UserID)
			const response = await createUserApi(added);
			const newUserId = response.UserId; // API returns UserID
			
			// 2. Apply folder permissions if any were set
			if (draftFolderPermissions.length > 0 && newUserId) {
				try {
					const payload = prepareFolderPermissionsPayload(draftFolderPermissions, newUserId, false);
					await setUserFolderPermissions(payload);
					notifySuccess(
						`User created successfully with ${draftFolderPermissions.length} folder permissions applied`
					);
				} catch (permissionError) {
					notifyError(
						`User created successfully, but folder permissions failed: ${permissionError.message}`
					);
				}
			} else {
				notifySuccess(NOTIF_USER_CREATE_SUCCESS);
			}
			
			await initAfterAddingUser();
			dispatch({ type: RESET, payload: initUser });
			
		} catch (error) {
			notifyError(error.message);
		} finally {
			setIsLoading(false);
		}
	};

	// Keep the original onCreateUser for backward compatibility
	const onCreateUser = async () => {
		await onCreateUserWithPermissions([]);
	};

	const validateBeforeSaving = () => {
		const _canNotSave = checkUserValid(user, setValid);
		if (_canNotSave) {
			notifyError('Please correct the errors before saving.');
		}
		return !_canNotSave;
	};

	useEffect(() => {
		(async () => {
			try {
				if (id !== '') {
					setIsLoading(true);
					const data = await getContactApi(id);
					const userObj = convertFromContactToUserObj(
						data,
						companies,
						contactTypes,
						languages,
					);
					checkUserValid(userObj, setValid);
					dispatch({ type: USER_CONVERT, payload: userObj });
				}
			} catch (error) {
				notifyError(error.message);
			} finally {
				setIsLoading(false);
			}
		})();
	}, [companies, contactTypes, id, languages, notifyError]);

	useEffect(() => {
		(async () => {
			try {
				setIsLoading(true);
				const data = await getAllContactsApi();
				const converted = await convertExistList(data);
				reduxDispatch(storeAllContacts(converted));
			} catch (error) {
				notifyError(error.message);
			} finally {
				setIsLoading(false);
			}
		})();
	}, [notifyError, reduxDispatch]);

	return {
		isLoading,
		user,
		valid,
		addressType,
		dispatch,
		handleForm,
		onValid,
		onTogBtn,
		onCreateUser,
		onCreateUserWithPermissions,
		onResetBtn,
	};
};

export default useUserAddForm;
